import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Palette,
  Code,
  Activity,
  Server,
  Zap,
  Wifi,
} from 'lucide-react';

const PRODUCT_SHELL = {
  left: {
    id: 'left',
    Icon: Palette,
    colors: {
      gradient: 'from-[#2568ff] via-blue-600 to-indigo-950',
      glow: 'bg-[#47b0ff]',
      ring: 'border-l-[#47b0ff]/60',
      bar: 'left-0 bg-[#47b0ff]',
    },
  },
  right: {
    id: 'right',
    Icon: Code,
    colors: {
      gradient: 'from-emerald-600 to-teal-900',
      glow: 'bg-emerald-500',
      ring: 'border-r-emerald-500/50',
      bar: 'right-0 bg-emerald-500',
    },
  },
};

const ANIMATIONS = {
  container: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.1 },
    },
    exit: { opacity: 0, transition: { duration: 0.2 } },
  },
  item: {
    hidden: { opacity: 0, y: 20, filter: 'blur(10px)' },
    visible: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: { type: 'spring', stiffness: 100, damping: 20 },
    },
    exit: { opacity: 0, y: -10, filter: 'blur(5px)' },
  },
  icon: (isLeft) => ({
    initial: {
      opacity: 0,
      scale: 1.5,
      filter: 'blur(15px)',
      rotate: isLeft ? -30 : 30,
      x: isLeft ? -80 : 80,
    },
    animate: {
      opacity: 1,
      scale: 1,
      filter: 'blur(0px)',
      rotate: 0,
      x: 0,
      transition: { type: 'spring', stiffness: 260, damping: 20 },
    },
    exit: {
      opacity: 0,
      scale: 0.6,
      filter: 'blur(20px)',
      transition: { duration: 0.25 },
    },
  }),
};

const BackgroundGradient = ({ isLeft }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    <motion.div
      animate={{
        background: isLeft
          ? 'radial-gradient(circle at 0% 50%, rgba(37, 104, 255, 0.35), transparent 50%)'
          : 'radial-gradient(circle at 100% 50%, rgba(16, 185, 129, 0.15), transparent 50%)',
      }}
      transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
      className="absolute inset-0"
    />
  </div>
);

const ProductVisual = ({ data, isLeft }) => {
  const Icon = data.Icon;
  return (
    <motion.div layout="position" className="relative group shrink-0">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
        className={`absolute inset-[-20%] rounded-full border border-dashed border-white/10 ${data.colors.ring}`}
      />
      <motion.div
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        className={`absolute inset-0 rounded-full bg-gradient-to-br ${data.colors.gradient} blur-2xl opacity-40`}
      />

      <div className="relative h-72 w-72 md:h-[420px] md:w-[420px] rounded-full border border-white/5 shadow-2xl flex items-center justify-center overflow-hidden bg-black/20 backdrop-blur-sm">
        <motion.div
          animate={{ y: [-10, 10, -10] }}
          transition={{ repeat: Infinity, duration: 6, ease: 'easeInOut' }}
          className="relative z-10 w-full h-full flex items-center justify-center"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={data.id}
              variants={ANIMATIONS.icon(isLeft)}
              initial="initial"
              animate="animate"
              exit="exit"
              className="flex items-center justify-center"
            >
              <Icon
                className="w-40 h-40 md:w-56 md:h-56 text-white drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
                strokeWidth={1.1}
              />
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>

      <motion.div
        layout="position"
        className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap"
      >
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 bg-zinc-950/80 px-4 py-2 rounded-full border border-white/5 backdrop-blur">
          <span
            className={`h-1.5 w-1.5 rounded-full ${data.colors.glow} animate-pulse`}
          />
          {data.stats.status}
        </div>
      </motion.div>
    </motion.div>
  );
};

const ProductDetails = ({ data, isLeft, pillarSuffix }) => {
  const alignClass = isLeft ? 'items-start text-left' : 'items-end text-right';
  const flexDirClass = isLeft ? 'flex-row' : 'flex-row-reverse';

  return (
    <motion.div
      variants={ANIMATIONS.container}
      initial="hidden"
      animate="visible"
      exit="exit"
      className={`flex flex-col ${alignClass}`}
    >
      <motion.h2
        variants={ANIMATIONS.item}
        className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-400 mb-2"
      >
        {data.label} {pillarSuffix}
      </motion.h2>
      <motion.h1
        variants={ANIMATIONS.item}
        className="text-4xl md:text-5xl font-bold tracking-tight mb-2 text-transparent bg-clip-text bg-gradient-to-b from-white to-zinc-500"
      >
        {data.title}
      </motion.h1>
      <motion.p
        variants={ANIMATIONS.item}
        className={`text-zinc-300 mb-8 max-w-sm leading-relaxed ${
          isLeft ? 'mr-auto' : 'ml-auto'
        }`}
      >
        {data.description}
      </motion.p>

      <motion.div
        variants={ANIMATIONS.item}
        className="w-full space-y-6 bg-zinc-900/40 p-6 rounded-2xl border border-white/5 backdrop-blur-sm"
      >
        {data.features.map((feature, idx) => {
          const FeatureIcon = feature.icon;
          return (
            <div key={feature.label} className="group">
              <div
                className={`flex items-center justify-between mb-3 text-sm ${flexDirClass}`}
              >
                <div
                  className={`flex items-center gap-2 ${
                    feature.value > 50 ? 'text-zinc-200' : 'text-zinc-400'
                  }`}
                >
                  <FeatureIcon size={16} /> <span>{feature.label}</span>
                </div>
                <span className="font-mono text-xs text-zinc-400">
                  {feature.value}%
                </span>
              </div>
              <div className="relative h-2 w-full bg-zinc-800 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${feature.value}%` }}
                  transition={{ duration: 1, delay: 0.4 + idx * 0.15 }}
                  className={`absolute top-0 bottom-0 ${data.colors.bar} opacity-80`}
                />
              </div>
            </div>
          );
        })}
      </motion.div>

      <motion.div
        variants={ANIMATIONS.item}
        className={`mt-6 flex items-center gap-3 text-zinc-400 ${flexDirClass}`}
      >
        <span
          className={`h-1.5 w-1.5 rounded-full ${data.colors.glow} animate-pulse`}
        />
        <span className="text-sm font-medium">{data.stats.meta}</span>
      </motion.div>
    </motion.div>
  );
};

const Switcher = ({ activeId, onToggle, options }) => {
  return (
    <div className="absolute bottom-10 inset-x-0 flex justify-center z-20 pointer-events-none">
      <motion.div
        layout
        className="pointer-events-auto flex items-center gap-1 p-1.5 rounded-full bg-zinc-900/80 backdrop-blur-2xl border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.6)] ring-1 ring-white/5"
      >
        {options.map((opt) => (
          <motion.button
            key={opt.id}
            type="button"
            onClick={() => onToggle(opt.id)}
            whileTap={{ scale: 0.96 }}
            className="relative w-24 h-12 rounded-full flex items-center justify-center text-sm font-medium focus:outline-none"
          >
            {activeId === opt.id && (
              <motion.div
                layoutId="island-surface"
                className="absolute inset-0 rounded-full bg-gradient-to-b from-white/10 to-white/5 shadow-inner"
                transition={{ type: 'spring', stiffness: 220, damping: 22 }}
              />
            )}
            <span
              className={`relative z-10 transition-colors duration-300 ${
                activeId === opt.id
                  ? 'text-white'
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              {opt.label}
            </span>
            {activeId === opt.id && (
              <motion.span
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute -bottom-1 h-1 w-6 rounded-full bg-gradient-to-r from-transparent via-white/60 to-transparent"
              />
            )}
          </motion.button>
        ))}
      </motion.div>
    </div>
  );
};

export default function SpatialShowcase() {
  const { t, i18n } = useTranslation();
  const [activeSide, setActiveSide] = useState('left');

  const productData = useMemo(
    () => ({
      left: {
        ...PRODUCT_SHELL.left,
        label: t('about.spatial.left.label'),
        title: t('about.spatial.left.title'),
        description: t('about.spatial.left.description'),
        stats: {
          status: t('about.spatial.left.status'),
          meta: t('about.spatial.left.meta'),
        },
        features: [
          {
            label: t('about.spatial.left.featureBrand'),
            value: 98,
            icon: Wifi,
          },
          {
            label: t('about.spatial.left.featureIterate'),
            value: 95,
            icon: Zap,
          },
        ],
      },
      right: {
        ...PRODUCT_SHELL.right,
        label: t('about.spatial.right.label'),
        title: t('about.spatial.right.title'),
        description: t('about.spatial.right.description'),
        stats: {
          status: t('about.spatial.right.status'),
          meta: t('about.spatial.right.meta'),
        },
        features: [
          {
            label: t('about.spatial.right.featurePerf'),
            value: 96,
            icon: Activity,
          },
          {
            label: t('about.spatial.right.featureUptime'),
            value: 99,
            icon: Server,
          },
        ],
      },
    }),
    [t, i18n.language],
  );

  const currentData = productData[activeSide];
  const isLeft = activeSide === 'left';
  const pillarSuffix = t('about.spatial.pillarWord');
  return (
    <div className="relative min-h-screen w-full bg-black text-zinc-100 overflow-hidden selection:bg-zinc-800 flex flex-col items-center justify-center">
      <BackgroundGradient isLeft={isLeft} />

      <main className="relative z-10 w-full px-6 py-24 md:py-12 flex flex-col justify-center max-w-7xl mx-auto">
        <motion.div
          layout
          transition={{ type: 'spring', bounce: 0, duration: 0.9 }}
          className={`flex flex-col md:flex-row items-center justify-center gap-12 md:gap-32 lg:gap-48 w-full ${
            isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
          }`}
        >
          <ProductVisual data={currentData} isLeft={isLeft} />

          <motion.div layout="position" className="w-full max-w-md">
            <AnimatePresence mode="wait">
              <ProductDetails
                key={activeSide}
                data={currentData}
                isLeft={isLeft}
                pillarSuffix={pillarSuffix}
              />
            </AnimatePresence>
          </motion.div>
        </motion.div>
      </main>

      <Switcher
        activeId={activeSide}
        onToggle={setActiveSide}
        options={[productData.left, productData.right]}
      />
    </div>
  );
}
